import { usePermissionsContext } from '../context/permissions-context';

// Main hook for permission checking
export const usePermissions = () => {
  const context = usePermissionsContext();
  return context;
};

// Hook for checking specific permission
export const usePermission = (permissionSlug: string, entryPoint?: string, groupName?: string, action?: string) => {
  const { hasPermission, showPermissionDenied } = usePermissionsContext();

  const checkPermission = () => {
    const hasAccess = hasPermission(permissionSlug, entryPoint, groupName, action);
    if (!hasAccess) {
      showPermissionDenied(permissionSlug);
    }
    return hasAccess;
  };

  return {
    hasPermission: hasPermission(permissionSlug, entryPoint, groupName, action),
    checkPermission,
    showPermissionDenied: () => showPermissionDenied(permissionSlug),
  };
};

// Hook for checking permission by object
export const usePermissionByObject = (permissionObject: { slug: string; entryPoint?: string; groupName?: string; action?: string }) => {
  const { hasPermission, showPermissionDenied } = usePermissionsContext();

  const checkPermission = () => {
    const hasAccess = hasPermission(permissionObject.slug, permissionObject.entryPoint, permissionObject.groupName, permissionObject.action);
    if (!hasAccess) {
      showPermissionDenied(permissionObject.slug);
    }
    return hasAccess;
  };

  return {
    hasPermission: hasPermission(permissionObject.slug, permissionObject.entryPoint, permissionObject.groupName, permissionObject.action),
    checkPermission,
    showPermissionDenied: () => showPermissionDenied(permissionObject.slug),
  };
};

// Hook for checking multiple permissions (any)
export const useAnyPermission = (permissionSlugs: string[]) => {
  const { hasAnyPermission, showPermissionDenied } = usePermissionsContext();

  const checkAnyPermission = () => {
    const hasAccess = hasAnyPermission(permissionSlugs);
    if (!hasAccess) {
      showPermissionDenied(permissionSlugs.join(' OR '));
    }
    return hasAccess;
  };

  return {
    hasAnyPermission: hasAnyPermission(permissionSlugs),
    checkAnyPermission,
    showPermissionDenied: () => showPermissionDenied(permissionSlugs.join(' OR ')),
  };
};

// Hook for checking multiple permissions (all)
export const useAllPermissions = (permissionSlugs: string[]) => {
  const { hasAllPermissions, showPermissionDenied } = usePermissionsContext();

  const checkAllPermissions = () => {
    const hasAccess = hasAllPermissions(permissionSlugs);
    if (!hasAccess) {
      showPermissionDenied(permissionSlugs.join(' AND '));
    }
    return hasAccess;
  };

  return {
    hasAllPermissions: hasAllPermissions(permissionSlugs),
    checkAllPermissions,
    showPermissionDenied: () => showPermissionDenied(permissionSlugs.join(' AND ')),
  };
};

// Hook for checking if user is developer
export const useIsDeveloper = () => {
  const { isDeveloper } = usePermissionsContext();
  return isDeveloper;
};

// Hook for checking permission by slug dynamically
export const usePermissionBySlug = (permissionSlug: string) => {
  const { permissions, hasPermission, showPermissionDenied } = usePermissionsContext();

  // Find permission by slug
  const permission = permissions.find(p => p.slug === permissionSlug);

  const checkPermission = () => {
    if (!permission) {
      showPermissionDenied(permissionSlug);
      return false;
    }

    const hasAccess = hasPermission(
      permission.slug,
      permission.entry_point,
      permission.group_name,
      permission.action
    );

    if (!hasAccess) {
      showPermissionDenied(permissionSlug);
    }
    return hasAccess;
  };

  return {
    hasPermission: permission ? hasPermission(
      permission.slug,
      permission.entry_point,
      permission.group_name,
      permission.action
    ) : false,
    checkPermission,
    showPermissionDenied: () => showPermissionDenied(permissionSlug),
    permission, // Return found permission for additional information
  };
};

// Hook for checking multiple permissions by slugs dynamically
export const usePermissionsBySlugs = (permissionSlugs: string[]) => {
  const { permissions, hasAnyPermission, hasAllPermissions, showPermissionDenied } = usePermissionsContext();

  // Find permissions by slugs
  const foundPermissions = permissions.filter(p => permissionSlugs.includes(p.slug));

  const checkAnyPermission = () => {
    if (foundPermissions.length === 0) {
      showPermissionDenied(permissionSlugs.join(' OR '));
      return false;
    }

    const hasAccess = hasAnyPermission(permissionSlugs);
    if (!hasAccess) {
      showPermissionDenied(permissionSlugs.join(' OR '));
    }
    return hasAccess;
  };

  const checkAllPermissions = () => {
    if (foundPermissions.length !== permissionSlugs.length) {
      showPermissionDenied(permissionSlugs.join(' AND '));
      return false;
    }

    const hasAccess = hasAllPermissions(permissionSlugs);
    if (!hasAccess) {
      showPermissionDenied(permissionSlugs.join(' AND '));
    }
    return hasAccess;
  };

  return {
    hasAnyPermission: hasAnyPermission(permissionSlugs),
    hasAllPermissions: hasAllPermissions(permissionSlugs),
    checkAnyPermission,
    checkAllPermissions,
    showPermissionDenied: () => showPermissionDenied(permissionSlugs.join(' OR ')),
    foundPermissions, // Return found rights
  };
};